// Página OfferForm - Crear/Editar oferta
// Permite crear lotes de ofertas con selección de categorías y productos

import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useOffer, useOfferOperations } from '../../hooks/useOffers'
import { useCategories } from '../../hooks/useCategories'
import { useProducts } from '../../hooks/useFirestore'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import MultiSelect from '../../components/MultiSelect'
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../../utils/helpers'
import './OfferForm.css'

/**
 * Página OfferForm
 * Formulario para crear o editar ofertas
 */
function OfferForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { userData } = useAuth()
  const isEditing = !!id

  // Hooks
  const { offer, loading: loadingOffer } = useOffer(id)
  const { create, update, loading: saving } = useOfferOperations()
  const { categories } = useCategories()
  const { products: allProducts } = useProducts({})

  // Estados
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    active: true,
    categories: [],
    products: []
  })
  const [errors, setErrors] = useState({})
  const [notification, setNotification] = useState(null)
  const [editingFields, setEditingFields] = useState({}) // { productId: Set of field names }

  // Cargar datos de la oferta si está editando
  useEffect(() => {
    if (isEditing && offer && !loadingOffer) {
      const startDate = offer.startDate?.toDate ? offer.startDate.toDate() : new Date(offer.startDate)
      const endDate = offer.endDate?.toDate ? offer.endDate.toDate() : new Date(offer.endDate)
      
      setFormData({
        name: offer.name || '',
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        active: offer.active !== false,
        categories: offer.categories || [],
        products: offer.products || []
      })
    }
  }, [isEditing, offer, loadingOffer])

  // Obtener productos de las categorías seleccionadas
  const categoryProducts = useMemo(() => {
    if (!formData.categories || formData.categories.length === 0) return []
    
    return allProducts.filter(product => {
      const productCategoryId = product.categoryId || product.category
      return formData.categories.includes(productCategoryId) && product.active !== false
    })
  }, [allProducts, formData.categories])

  // Generar lista de productos para la oferta basada en categorías seleccionadas
  useEffect(() => {
    if (formData.categories.length > 0 && categoryProducts.length > 0) {
      // Solo agregar productos nuevos que no estén ya en la lista
      const existingProductIds = formData.products.map(p => p.productId)
      const newProducts = categoryProducts
        .filter(p => !existingProductIds.includes(p.id))
        .map(product => {
          const existingProduct = formData.products.find(p => p.productId === product.id)
          if (existingProduct) return existingProduct

          return {
            productId: product.id,
            productName: product.name,
            categoryId: product.categoryId || product.category,
            categoryName: categories.find(c => c.id === (product.categoryId || product.category))?.name || 'Sin categoría',
            originalPrice: product.price || 0,
            discountType: 'percentage', // 'percentage' o 'fixed'
            discountValue: 0,
            finalPrice: product.price || 0,
            excluded: false
          }
        })

      if (newProducts.length > 0) {
        setFormData(prev => ({
          ...prev,
          products: [...prev.products, ...newProducts]
        }))
      }
    }
  }, [formData.categories, formData.products, categoryProducts, categories])

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  // Manejar selección de categorías
  const handleCategoriesChange = (selectedCategoryIds) => {
    setFormData(prev => ({
      ...prev,
      categories: selectedCategoryIds
    }))
  }

  // Manejar cambios en productos de la oferta
  const handleProductChange = (productId, field, value) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p => {
        if (p.productId === productId) {
          const updated = { ...p, [field]: value }
          
          // Recalcular precio final si cambió descuento
          if (field === 'discountType' || field === 'discountValue') {
            const discountValue = field === 'discountValue' ? parseFloat(value) || 0 : p.discountValue
            const discountType = field === 'discountType' ? value : p.discountType
            
            if (discountType === 'percentage') {
              updated.finalPrice = updated.originalPrice * (1 - discountValue / 100)
            } else {
              updated.finalPrice = Math.max(0, updated.originalPrice - discountValue)
            }
          }
          
          // Si cambió el precio final manualmente
          if (field === 'finalPrice') {
            const finalPrice = typeof value === 'number' ? value : (parseFloat(value) || 0)
            updated.finalPrice = finalPrice
            // Recalcular descuento
            if (updated.originalPrice > 0) {
              const discount = updated.originalPrice - finalPrice
              updated.discountValue = discount
              updated.discountType = 'fixed'
            }
          }
          
          return updated
        }
        return p
      })
    }))
  }

  // Toggle excluir producto
  const handleToggleExclude = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p.productId === productId ? { ...p, excluded: !p.excluded } : p
      )
    }))
  }

  // Remover producto de la oferta
  const handleRemoveProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter(p => p.productId !== productId)
    }))
  }

  // Validar formulario
  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    }

    if (!formData.startDate) {
      newErrors.startDate = 'La fecha de inicio es requerida'
    }

    if (!formData.endDate) {
      newErrors.endDate = 'La fecha de fin es requerida'
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate)
      const end = new Date(formData.endDate)
      if (end <= start) {
        newErrors.endDate = 'La fecha de fin debe ser posterior a la fecha de inicio'
      }
    }

    if (formData.categories.length === 0) {
      newErrors.categories = 'Debe seleccionar al menos una categoría'
    }

    if (formData.products.filter(p => !p.excluded).length === 0) {
      newErrors.products = 'Debe incluir al menos un producto en la oferta'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!validate()) {
      setNotification({
        type: 'error',
        message: 'Por favor, corrige los errores en el formulario'
      })
      setTimeout(() => setNotification(null), 5000)
      return
    }

    if (saving) return

    try {
      const offerData = {
        name: formData.name.trim(),
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        active: formData.active,
        categories: formData.categories,
        products: formData.products,
        createdBy: userData?.uid,
        createdByName: userData?.name || userData?.displayName || 'Sistema'
      }

      if (isEditing) {
        await update(id, offerData)
        setNotification({
          type: 'success',
          message: 'Oferta actualizada correctamente'
        })
      } else {
        await create(offerData)
        setNotification({
          type: 'success',
          message: 'Oferta creada correctamente'
        })
      }

      setTimeout(() => {
        navigate('/admin/ofertas')
      }, 1500)
    } catch (error) {
      console.error('Error guardando oferta:', error)
      setNotification({
        type: 'error',
        message: error.message || 'Error al guardar la oferta'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Agrupar productos por categoría
  const productsByCategory = useMemo(() => {
    const grouped = {}
    formData.products.forEach(product => {
      const categoryName = product.categoryName || 'Sin categoría'
      if (!grouped[categoryName]) {
        grouped[categoryName] = []
      }
      grouped[categoryName].push(product)
    })
    return grouped
  }, [formData.products])

  if (isEditing && loadingOffer) {
    return (
      <div className="offer-form-page">
        <div className="loading-state">
          <p>Cargando oferta...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="offer-form-page">
      <div className="form-header">
        <h2>{isEditing ? 'Editar Oferta' : 'Nueva Oferta'}</h2>
        <Button variant="outline" onClick={() => navigate('/admin/ofertas')}>
          ← Volver
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="offer-form">
        {/* Información General */}
        <div className="form-section">
          <h3 className="section-title">Información General</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">
                Nombre de la Oferta <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ej: Oferta de Verano 2025"
                className={errors.name ? 'error' : ''}
                disabled={saving}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="startDate">
                Fecha de Inicio <span className="required">*</span>
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className={errors.startDate ? 'error' : ''}
                disabled={saving}
              />
              {errors.startDate && <span className="error-message">{errors.startDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="endDate">
                Fecha de Fin <span className="required">*</span>
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className={errors.endDate ? 'error' : ''}
                disabled={saving}
              />
              {errors.endDate && <span className="error-message">{errors.endDate}</span>}
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  disabled={saving}
                />
                Activa
              </label>
            </div>
          </div>
        </div>

        {/* Selección de Categorías */}
        <div className="form-section">
          <h3 className="section-title">Categorías</h3>
          <div className="form-group">
            <label>
              Seleccionar Categorías <span className="required">*</span>
            </label>
            <MultiSelect
              options={categories
                .filter(cat => cat.active !== false)
                .map(cat => ({
                  id: cat.id,
                  label: cat.name
                }))}
              selected={formData.categories}
              onChange={handleCategoriesChange}
              placeholder="Selecciona las categorías para la oferta..."
            />
            {errors.categories && <span className="error-message">{errors.categories}</span>}
            <small className="form-hint">
              Los productos de las categorías seleccionadas se agregarán automáticamente a la oferta
            </small>
          </div>
        </div>

        {/* Productos de la Oferta */}
        <div className="form-section">
          <h3 className="section-title">
            Productos de la Oferta ({formData.products.filter(p => !p.excluded).length} productos)
          </h3>
          {errors.products && <span className="error-message">{errors.products}</span>}

          {Object.keys(productsByCategory).length > 0 ? (
            Object.entries(productsByCategory).map(([categoryName, products]) => (
              <div key={categoryName} className="category-products-section">
                <h4 className="category-title">{categoryName}</h4>
                <div className="products-table-container">
                  <table className="products-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Precio Original</th>
                        <th>Tipo Descuento</th>
                        <th>Valor Descuento</th>
                        <th>Precio Final</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.productId} className={product.excluded ? 'excluded-row' : ''}>
                          <td>
                            <strong>{product.productName}</strong>
                            {product.excluded && <span className="excluded-badge">Excluido</span>}
                          </td>
                          <td>{formatCurrency(product.originalPrice)}</td>
                          <td>
                            <select
                              value={product.discountType}
                              onChange={(e) => handleProductChange(product.productId, 'discountType', e.target.value)}
                              disabled={saving || product.excluded}
                              className="discount-type-select"
                            >
                              <option value="percentage">Porcentaje (%)</option>
                              <option value="fixed">Monto Fijo ($)</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              value={product.discountValue}
                              onChange={(e) => handleProductChange(product.productId, 'discountValue', e.target.value)}
                              min="0"
                              step={product.discountType === 'percentage' ? '1' : '0.01'}
                              disabled={saving || product.excluded}
                              className="discount-value-input"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={
                                editingFields[product.productId]?.has('finalPrice')
                                  ? (product.finalPrice ? `$${product.finalPrice.replace('.', ',')}` : '')
                                  : (product.finalPrice ? formatCurrencyInput(product.finalPrice, true) : '')
                              }
                              onChange={(e) => {
                                let cleanedValue = e.target.value.replace(/[^0-9.,]/g, '')
                                if (!cleanedValue) {
                                  handleProductChange(product.productId, 'finalPrice', '')
                                  return
                                }
                                cleanedValue = cleanedValue.replace('.', ',')
                                const parts = cleanedValue.split(',')
                                if (parts.length > 2) {
                                  cleanedValue = parts[0] + ',' + parts.slice(1).join('')
                                }
                                handleProductChange(product.productId, 'finalPrice', cleanedValue)
                              }}
                              onFocus={() => {
                                setEditingFields(prev => ({
                                  ...prev,
                                  [product.productId]: new Set(prev[product.productId] || []).add('finalPrice')
                                }))
                              }}
                              onBlur={() => {
                                const numericValue = parseCurrencyInput(product.finalPrice || '0')
                                const finalPriceStr = numericValue > 0 ? numericValue.toString() : ''
                                handleProductChange(product.productId, 'finalPrice', finalPriceStr)
                                setEditingFields(prev => {
                                  const newSet = { ...prev }
                                  if (newSet[product.productId]) {
                                    newSet[product.productId] = new Set(newSet[product.productId])
                                    newSet[product.productId].delete('finalPrice')
                                  }
                                  return newSet
                                })
                              }}
                              disabled={saving || product.excluded}
                              className="final-price-input"
                              placeholder="$0"
                            />
                          </td>
                          <td>
                            <div className="product-actions">
                              <Button
                                type="button"
                                variant={product.excluded ? 'outline' : 'danger'}
                                size="small"
                                onClick={() => handleToggleExclude(product.productId)}
                                disabled={saving}
                              >
                                {product.excluded ? '✓ Incluir' : '✕ Excluir'}
                              </Button>
                              <Button
                                type="button"
                                variant="danger"
                                size="small"
                                onClick={() => handleRemoveProduct(product.productId)}
                                disabled={saving}
                              >
                                🗑️ Quitar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          ) : (
            <p className="no-products-message">
              Selecciona categorías para ver los productos disponibles
            </p>
          )}
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/ofertas')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : isEditing ? 'Actualizar Oferta' : 'Guardar Oferta'}
          </Button>
        </div>
      </form>

      {/* Notificaciones */}
      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default OfferForm

